import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/apiClient";
import { Loader2, ArrowLeft, Zap, ShieldCheck, Trash2 } from "lucide-react";

const PERM_LABELS = {
  view_profile: "Profil", view_projects: "Projets", view_progress: "Progression",
  view_statistics: "Statistiques", validate_projects: "Validation", manage_tasks: "Tâches", manage_goals: "Objectifs",
};

export default function ManagerArtistDetail() {
  const { id } = useParams();
  const { identity } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => { if (identity && !identity.roles?.includes("MANAGER")) navigate("/become-manager"); }, [identity, navigate]);
  useEffect(() => { api.get(`/manager/artists/${id}`).then((r) => setD(r.data)).catch(() => setErr(true)); }, [id]);

  const remove = async () => {
    if (!window.confirm("Retirer cet artiste de ton espace ?")) return;
    await api.delete(`/manager/artists/${id}`);
    navigate("/manager/artists");
  };

  if (err) return <div className="text-center py-20 text-zinc-500" data-testid="artist-detail-error">Membre introuvable ou non autorisé.</div>;
  if (!d) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="manager-artist-detail">
      <button onClick={() => navigate("/manager/artists")} data-testid="detail-back" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-200"><ArrowLeft className="w-4 h-4" /> Mes membres</button>

      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6 flex items-center gap-5">
        <div className="w-20 h-20 rounded-2xl overflow-hidden bg-zinc-900 flex items-center justify-center border-2 border-amber-500/40 shrink-0">
          {d.avatar_url ? <img src={d.avatar_url} alt="" className="w-full h-full object-cover" /> : <span className="font-display text-3xl font-extrabold gold-text">{(d.name || "A")[0].toUpperCase()}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-2xl font-extrabold truncate" data-testid="detail-name">{d.name}</h1>
          <div className="flex items-center gap-3 text-sm text-zinc-400 mt-1">
            <span>Niveau {d.level}</span>
            <span className="flex items-center gap-1"><Zap className="w-4 h-4 text-amber-400" />{d.xp} XP</span>
            {typeof d.score === "number" && <span>Score {d.score}</span>}
          </div>
          {d.last_activity && <p className="text-xs text-zinc-600 mt-1">Dernière activité : {d.last_activity}</p>}
        </div>
      </div>

      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-5">
        <div className="flex items-center gap-2 text-amber-500 mb-3"><ShieldCheck className="w-4 h-4" /><span className="text-xs font-mono uppercase tracking-wider">Accès accordés</span></div>
        <div className="flex flex-wrap gap-2" data-testid="detail-permissions">
          {(d.permissions || []).map((p) => (
            <span key={p} className="text-xs px-3 py-1 rounded-full bg-zinc-900 border border-zinc-700 text-zinc-300">{PERM_LABELS[p] || p}</span>
          ))}
        </div>
        <p className="text-[11px] text-zinc-600 mt-3">L'artiste reste propriétaire de ses données. Tu vois uniquement ce qu'il a autorisé (lecture seule par défaut).</p>
      </div>

      {d.projects && (
        <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-5" data-testid="detail-projects">
          <h2 className="font-display text-lg font-bold mb-3">Projets</h2>
          {d.projects.length === 0 ? <p className="text-sm text-zinc-500">Aucun projet.</p> : (
            <div className="space-y-2">
              {d.projects.map((p) => (
                <div key={p.id} className="flex items-center justify-between bg-[#0F0F13] border border-zinc-800 rounded-xl p-3 text-sm">
                  <span className="text-zinc-200 truncate">{p.title}</span>
                  <span className="text-xs text-zinc-500">{p.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <button onClick={remove} data-testid="detail-remove-artist" className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300 px-4 h-11 rounded-xl border border-red-500/30 hover:bg-red-500/10">
        <Trash2 className="w-4 h-4" /> Retirer cet artiste
      </button>
    </div>
  );
}
